import { Select, Image, Modal, Input, Tabs, Popconfirm } from 'antd'
import { FilePdfOutlined, FileTextOutlined, DeleteOutlined } from '@ant-design/icons'
import styles from './PatientChatBubble.module.scss'
import * as timeago from 'timeago.js'
import { useEffect, useState, useRef } from 'react'
import { FaUserCircle } from 'react-icons/fa'
import { FaUserDoctor } from 'react-icons/fa6'
import { useDispatch, useSelector } from 'react-redux'
import { get, ref, push, set, update } from 'firebase/database'
import { database } from 'utils/firebase'
import { Tooltip, message, Button } from 'antd';
import dayjs from 'dayjs'
import { setTranscriptionState, setMeetMessageId, setForceStop, setMeetButtonClicked, setStopTranscription } from 'slices/transcription.slice';
import useScopes from 'hooks/useScopes'
import { Table } from 'react-bootstrap'
import ChunkPlayer from 'pages/patient/consultationDetails/Consultation/LogsModal/ChunkPlayer'

const { TextArea } = Input
const { Option } = Select
const { TabPane } = Tabs

const PatientChatBubble = (props) => {
  const PHD_TIMEZONE = useSelector((state) => state.timeZone?.userTimeZone)
  const {
    patient,
    data,
    sessions,
  } = props

  const dispatch = useDispatch();
  // Get transcription state from Redux
  const { isTranscribing, stopKey } = useSelector(state => state.transcription);
  const { isAdmin } = useScopes()

  const { currentSession } = useSelector((state) => state.mentalHealth)
  const chatDiv = useRef(null)
  const [doctorData, setDoctorData] = useState(null)
  const [patientProfileImage, setPatientProfileImage] = useState(null)
  const [imageError, setImageError] = useState(false)
  const [transcriptModalVisible, setTranscriptModalVisible] = useState(false)
  const [editableEntries, setEditableEntries] = useState([])
  const [generatingSummary, setGeneratingSummary] = useState(false)
  const [activeTab, setActiveTab] = useState('transcript')
  const [editableSummary, setEditableSummary] = useState('')
  const [participantAction, setParticipantAction] = useState('doctor') // 'doctor', 'patient', or 'delete'
  const [audioChunks, setAudioChunks] = useState({})

  useEffect(() => {
    if (data.key && data.type === 'link') {
      dispatch(setMeetMessageId(data.key));
    }
  }, [dispatch, data])
  // Helper function to handle transcript button click
  const handleTranscriptToggle = () => {
    if (isTranscribing) {
      dispatch(setForceStop(true));  // Force stop first
      dispatch(setMeetMessageId(null));
      dispatch(setMeetButtonClicked(false));
      dispatch(setStopTranscription(true));
    } else {
      dispatch(setMeetMessageId(data.key));
      dispatch(setForceStop(false));
      dispatch(setMeetButtonClicked(true));
    }
  };

  const getDoctorDetail = async (docID) => {
    const doctorDetailRef = ref(database, `/doctors/${docID}`)

    try {
      const snapshot = await get(doctorDetailRef)
      if (snapshot.exists()) {
        const data = snapshot.val()
        setDoctorData(data)
      } else {
        console.log('Nooo')
      }
    } catch (error) {
      console.error('Error fetching data in chat:', error)
    }
  }

  const getPatientProfilePicture = async (patientId, owner) => {
    // Determine the path to the profile picture
    const profilePicturePath = owner
      ? `/users/${patientId}/profiles/${owner}/profile_image`
      : `/users/${patientId}/profile_image`;

    const profilePictureRef = ref(database, profilePicturePath)

    try {
      const snapshot = await get(profilePictureRef);
      if (snapshot.exists()) {
        const profileImage = snapshot.val();
        setPatientProfileImage(profileImage); // Update state with the profile image
      } else {
      }
    } catch (error) {
      console.error('Error fetching patient profile picture:', error);
    }
  };


  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  useEffect(() => {
    const objDiv = document.getElementById('chat')
    objDiv.scrollTop = objDiv.scrollHeight

    chatDiv.current.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    })
  }, [chatDiv, data])

  useEffect(() => {
    if (currentSession?.doctorID) {
      getDoctorDetail(currentSession?.doctorID)
    }
    if (currentSession?.patientID) {
      getPatientProfilePicture(
        currentSession.patientID,
        currentSession.owner
      );
    }
  }, [currentSession])

  const renderSessionTag = () => {
    if (!sessions || sessions.length === 0) return null
    const allSessions = [...sessions, ['current', currentSession]]

    const session = allSessions.find(
      ([_, sessionData]) => sessionData.firstMessageID === data.key,
    )

    if (session) {
      const timestamp = dayjs(session[1].timestamp).tz(PHD_TIMEZONE).format('MMMM D, YYYY h:mm:ss A');

      if (session[1].inbox) {
        return (
          <div className={styles.sessionTag}>
            <span className={styles.sessionText}>
              {`Query Messages at ${timestamp}`}{' '}
            </span>
          </div>
        )
      } else {
        const sessionIndex =
          allSessions
            .filter(([_, sessionData]) => !sessionData.inbox)
            .indexOf(session) + 1

        return (
          <div className={styles.sessionTag}>
            <span
              className={styles.sessionText}
            >{`Session ${sessionIndex} started at ${timestamp}`}</span>
            {/* <hr /> */}
          </div>
        )
      }
    }
    return null
  }

  // Parse and format transcript
  const parseTranscript = (transcriptText) => {
    if (!transcriptText || typeof transcriptText !== 'string') return []

    // Split by timestamp pattern (00:00:00)
    const lines = transcriptText.split(/(\d{2}:\d{2}:\d{2})/).filter(Boolean)
    const parsed = []

    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i].match(/\d{2}:\d{2}:\d{2}/) && lines[i + 1]) {
        const timestamp = lines[i]
        const content = lines[i + 1].trim()

        // Extract speaker and text
        const speakerMatch = content.match(/^\[(.+?)\]:(.*)/)
        if (speakerMatch) {
          parsed.push({
            timestamp,
            speaker: speakerMatch[1],
            text: speakerMatch[2].trim()
          })
        }
      }
    }

    return parsed
  }

  const handleShowTranscript = () => {
    // Only allow admins to edit transcript
    const entries = parseTranscript(data?.transcription || '')
    setEditableEntries(entries)
    // Use existing summary from data, or generate if not present
    const summary = data?.transcriptSummary || ""
    setEditableSummary(summary)
    setActiveTab('summary')
    setTranscriptModalVisible(true)
  }

  const handleEntryChange = (index, field, value) => {
    setEditableEntries(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const handleDeleteEntry = (index) => {
    setEditableEntries(prev => prev.filter((_, i) => i !== index))
    message.success('Entry deleted')
  }

  const handleGlobalReplace = (fromSpeaker, toSpeaker) => {
    let doctorCount = 0
    let patientCount = 0
    let participantCount = 0

    setEditableEntries(prev =>
      prev.map(entry => {
        // Swap speakers: Doctor <-> Patient (don't touch Participant)
        if (entry.speaker === 'Doctor') {
          doctorCount++
          return { ...entry, speaker: 'Patient' }
        }
        if (entry.speaker === 'Patient') {
          patientCount++
          return { ...entry, speaker: 'Doctor' }
        }
        if (entry.speaker === 'Participant') {
          participantCount++
        }
        return entry
      })
    )

    const totalChanged = doctorCount + patientCount
    if (totalChanged > 0) {
      const msg = `Swapped speakers: ${doctorCount} Doctor(s) → Patient, ${patientCount} Patient(s) → Doctor`
      const participantMsg = participantCount > 0 ? `. ${participantCount} Participant(s) unchanged.` : ''
      message.success(msg + participantMsg)
    } else {
      message.info('No entries found to swap')
    }
  }

  const handleParticipantAction = () => {
    if (participantAction === 'delete') {
      // Delete all Participant entries
      const beforeCount = editableEntries.length
      setEditableEntries(prev => prev.filter(entry => entry.speaker !== 'Participant'))
      const deletedCount = beforeCount - editableEntries.filter(e => e.speaker !== 'Participant').length

      if (deletedCount > 0) {
        message.success(`Deleted ${deletedCount} Participant entries`)
      } else {
        message.info('No Participant entries found to delete')
      }
    } else {
      // Convert Participant to Doctor or Patient
      const targetSpeaker = participantAction === 'doctor' ? 'Doctor' : 'Patient'
      let changedCount = 0

      setEditableEntries(prev =>
        prev.map(entry => {
          if (entry.speaker === 'Participant') {
            changedCount++
            return { ...entry, speaker: targetSpeaker }
          }
          return entry
        })
      )

      if (changedCount > 0) {
        message.success(`Changed ${changedCount} Participant(s) to ${targetSpeaker}`)
      } else {
        message.info('No Participant entries found to convert')
      }
    }
  }

  const handleSaveTranscript = async () => {
    // Show confirmation dialog
    Modal.confirm({
      title: 'Save Transcript Changes',
      content: 'Are you sure you want to save the changes? This will update the transcript permanently.',
      okText: 'Yes, Save',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const reconstructed = editableEntries
            .map(entry => `${entry.timestamp} [${entry.speaker}]:${entry.text}`)
            .join('')

          const messageRef = ref(database, `mentalHealthConversations/${currentSession.conversationID}/${data.key}`)

          // Update both transcript and summary
          await update(messageRef, {
            transcription: reconstructed,
            transcriptSummary: editableSummary // Save the edited summary
          })

          message.success('Transcript saved successfully!')
          setTranscriptModalVisible(false)
        } catch (error) {
          console.error('Error saving transcript:', error)
          message.error('Failed to save transcript')
        }
      }
    })
  }

  // Check if there are any Participant entries
  const hasParticipants = editableEntries.some(entry => entry.speaker === 'Participant')



  const handleSendSummary = async () => {
    if (!editableSummary.trim()) {
      message.warning('Summary is empty')
      return
    }

    if (!currentSession || currentSession?.inbox || currentSession?.isArchive) {
      message.error('Cannot send summary to this session')
      return
    }

    try {
      setGeneratingSummary(true)

      const messageRef = ref(database, `mentalHealthConversations/${currentSession.conversationID}/`)
      const newMessageRef = push(messageRef)

      await set(newMessageRef, {
        content: editableSummary,
        sender: 'Doctor',
        timestamp: Date.now(),
        type: 'text',
        doctorID: currentSession.doctorID,
        doctorName: doctorData?.doctorName || 'Doctor',
      })

      // Also save the summary to the original message node
      const originalMessageRef = ref(database, `mentalHealthConversations/${currentSession.conversationID}/${data.key}`)
      await update(originalMessageRef, {
        transcriptSummary: editableSummary
      })

      message.success('Summary sent to chat successfully!')
      setTranscriptModalVisible(false)
    } catch (error) {
      console.error('Error sending summary:', error)
      message.error('Failed to send summary to chat')
    } finally {
      setGeneratingSummary(false)
    }
  }

  useEffect(() => {
    // When the modal is closed, or required identifiers are missing, clear any existing chunks.
    if (!transcriptModalVisible) {
      setAudioChunks({})
      return
    }

    if (!currentSession?.conversationID || !data?.key) {
      setAudioChunks({})
      return
    }

    // Clear previous chunks before fetching new ones to avoid showing stale data.
    setAudioChunks({})

    get(ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${data.key}/chunks`))
      .then((snapshot) => {
        const raw = snapshot.val() || {}
        const valid = Object.fromEntries(
          Object.entries(raw).filter(([, chunk]) => {
            const url = typeof chunk === 'string' ? chunk : chunk?.url
            return typeof url === 'string' && url.trim().length > 0
          })
        )
        setAudioChunks(valid)
      })
      .catch(() => setAudioChunks({}))
  }, [transcriptModalVisible, currentSession?.conversationID, data?.key])

  const transcriptEntries = parseTranscript(data?.transcription)

  return (
    <>
      {renderSessionTag()}
      <div
        className={patient ? styles.chatBubble : styles.docBubble}
        ref={chatDiv}
        id="chat"
      >
        {patient ? (
          patientProfileImage ? (
            <div className={styles.imageSize}>
              <Image
                width={30}
                height={30}
                style={{ borderRadius: '50%' }}
                src={patientProfileImage}
              />
            </div>
          ) : (
            <div>
              <FaUserCircle
                style={{ color: '#df7467', width: '25px', height: '35px' }}
              />
            </div>
          )
          // <span>Texxt</span>
        ) : doctorData && doctorData.profileImageURL && !imageError ? (
          <div className={styles.imageSize}>
            <Image
              width={30}
              height={30}
              src={doctorData.profileImageURL}
              style={{ borderRadius: '50%' }}
              onError={() => setImageError(true)} // Handle broken image fallback
            />
          </div>
        ) : (
          <FaUserDoctor
            style={{ color: '#df7467', width: '25px', height: '35px' }}
          />
        )}

        <div className={patient ? styles.messageDiv : styles.docDiv}>
          {/* Transcript indicator - show on left side if transcript exists */}
          {data.transcription && (
            <div>
              <Button
                type="link"
                icon={<FileTextOutlined />}
                onClick={handleShowTranscript}
                className={styles.transcriptButton}
              >
                Transcript
              </Button>
            </div>
          )}

          {/* {console.log("data for MP#", data)} */}
          {data.type === 'application' ? (
            <>
              <div className={patient ? styles.text : styles.doctext}>
                <a
                  href={data.content}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FilePdfOutlined
                    style={{
                      fontSize: '24px',
                      color: 'red',
                      width: '40px',
                      height: '50px',
                    }}
                  />
                </a>
              </div>
            </>
          ) : data.type === 'image' ? (
            <div className={patient ? styles.text : styles.doctext}>
              <div className={styles.imageSize}>
                <Image width={200} src={data.content} />
                <div className={styles.copyLink}>
                  <Tooltip>
                    <span
                      onClick={() => {
                        navigator.clipboard.writeText(data.content)
                        message.success('Image link copied to clipboard!')
                      }}
                      style={{
                        cursor: 'pointer',
                        color: '#1890ff',
                        textDecoration: 'underline',
                        alignContent: 'center',
                      }}
                    >
                      Copy Link
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          ) : data.type === 'audio' ? (
            <div className={patient ? styles.patientVoiceNote : styles.docVoiceNote}>
              {/* {data?.doctorName && (<span className={styles.duration}>{formatDuration(data?.duration)}</span>)} */}
              <audio controls src={data.content}></audio>

            </div>
          )

            :
            data.type === 'link' ? (
              <div className={patient ? styles.text : styles.doctext}>
                {data.content.split(': ').length === 2 ? (
                  <>
                    <span>{data.content.split(': ')[0]}:</span>
                    <br />
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={data.content.split(': ')[1]}
                    >
                      {data.content.split(': ')[1]}
                    </a>
                  </>
                ) : (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={data.content}
                  >
                    {data.content}
                  </a>
                )}
                {data.type === 'link' && data.content.includes("meet.google") && (
                  <Button
                    type="button"
                    style={{ backgroundColor: '#df7467', borderColor: '#df7467', color: 'white', fontWeight: 'bold', marginLeft: '10px' }}
                    onClick={handleTranscriptToggle}
                    disabled={!currentSession || currentSession?.inbox || currentSession?.isArchive || isAdmin}
                  >
                    {isTranscribing ? 'Stop Transcript' : 'Start Transcript'}
                  </Button>
                )}
              </div>
            )
              : data.type === 'video' ? (
                <div>
                  <video controls width="300">
                    <source src={data.content} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>

                </div>
              ) : (
                <span
                  className={patient ? styles.text : styles.doctext}
                  dangerouslySetInnerHTML={{
                    __html: data?.content?.replace(/\n/g, "<br/>"),
                  }}
                ></span>

              )}
          <span className={patient ? styles.time : styles.docTime}>
            {data?.timestamp
              ? dayjs(data.timestamp)
                .tz(PHD_TIMEZONE)
                .format('MMMM D, YYYY h:mm:ss A')
              : 'No Timestamp Available'}
          </span>
        </div>
      </div>

      {/* Transcript Modal with Tabs */}
      <Modal
        title="Meeting Transcript"
        open={transcriptModalVisible}
        onCancel={() => setTranscriptModalVisible(false)}
        width={1000}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setTranscriptModalVisible(false)}>
            Close
          </Button>,
          activeTab === 'summary' ? (
            <Button
              key="send"
              type="primary"
              loading={generatingSummary}
              onClick={handleSendSummary}
              disabled={!currentSession || currentSession?.inbox || currentSession?.isArchive || isAdmin}
            >
              📋 Send Summary to Chat
            </Button>
          ) : (
            <Button
              key="save"
              type="primary"
              onClick={handleSaveTranscript}
            >
              Save Changes
            </Button>
          )
        ]}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} disabled={!isAdmin}>
          <TabPane tab="View Transcript" key="transcript">
            <div className={styles.transcriptModal}>
              <div className={styles.globalReplaceSection}>
                <div className={styles.sectionTitle}>
                  Quick Actions:
                </div>
                <div className={styles.buttonGroup}>
                  <Button
                    size="small"
                    onClick={() => handleGlobalReplace('Doctor', 'Patient')}
                  >
                    Swap Doctor ↔ Patient
                  </Button>
                </div>
              </div>

              {/* Participant Action Section - Only show if participants exist and admin */}
              {hasParticipants && (
                <div
                  className={styles.globalReplaceSection}
                  style={{
                    marginTop: '16px',
                    border: '1px solid #ff4d4f',
                    borderRadius: '4px',
                    padding: '12px',
                    backgroundColor: '#fff1f0'
                  }}
                >
                  <div className={styles.sectionTitle} style={{ color: '#fff1f0', fontWeight: 'bold' }}>
                    ⚠️ Handle Participants:
                  </div>
                  <div className={styles.buttonGroup} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Select
                      value={participantAction}
                      onChange={setParticipantAction}
                      size="small"
                      style={{ width: '150px' }}
                    >
                      <Option value="doctor">Convert to Doctor</Option>
                      <Option value="patient">Convert to Patient</Option>
                      <Option value="delete">Delete Entries</Option>
                    </Select>
                    <Button
                      size="small"
                      type="primary"
                      onClick={handleParticipantAction}
                      danger={participantAction === 'delete'}
                    >
                      ⚠️ {participantAction === 'delete' ? 'Delete All Participants' : 'Convert All Participants'}
                    </Button>
                  </div>
                </div>
              )}

              {Object.keys(audioChunks).length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <Table bordered size="sm" style={{ marginBottom: 0 }}>
                    <tbody>
                      {Object.keys(audioChunks).sort().map((ck) => (
                        <ChunkPlayer key={ck} chunkKey={ck} chunk={audioChunks[ck]} />
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className={styles.entriesContainer}>
                {editableEntries.length > 0 ? (
                  editableEntries.map((entry, index) => (
                    <div
                      key={index}
                      className={styles.entryCard}
                      style={entry.speaker === 'Participant' ? {
                        border: '2px solid #ff4d4f',
                        backgroundColor: '#fff1f0'
                      } : {}}
                    >
                      <div className={styles.entryHeader}>
                        <span
                          className={styles.timestamp}
                          style={entry.speaker === 'Participant' ? { color: '#fff1f0', fontWeight: 'bold' } : {}}
                        >
                          {entry.timestamp}
                        </span>
                        <Select
                          value={entry.speaker}
                          onChange={(value) => handleEntryChange(index, 'speaker', value)}
                          size="small"
                          className={styles.speakerSelect}
                          style={entry.speaker === 'Participant' ? {
                            borderColor: '#ff4d4f',
                          } : {}}
                        >
                          <Option value="Doctor">Doctor</Option>
                          <Option value="Patient">Patient</Option>
                          <Option value="Participant" style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                            ⚠️ Participant
                          </Option>
                        </Select>
                        <Popconfirm
                          title="Delete this entry?"
                          description="Are you sure you want to delete this transcript entry?"
                          onConfirm={() => handleDeleteEntry(index)}
                          okText="Yes"
                          cancelText="No"
                          disabled={!isAdmin}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            className={styles.deleteButton}
                            disabled={!isAdmin}
                          />
                        </Popconfirm>
                      </div>
                      <TextArea
                        value={entry.text}
                        onChange={(e) => handleEntryChange(index, 'text', e.target.value)}
                        placeholder="Enter transcript text..."
                        autoSize={{ minRows: 1, maxRows: 8 }}
                        className={styles.textArea}
                        style={entry.speaker === 'Participant' ? {
                          borderColor: '#ff4d4f',
                        } : {}}
                        disabled={!isAdmin}
                      />
                    </div>
                  ))
                ) : (
                  <p className={styles.emptyState}>
                    No transcript entries found wait for few seconds for AI to process.
                  </p>
                )}
              </div>
            </div>
          </TabPane>

          <TabPane tab="Summary" key="summary">
            <div className={styles.transcriptModal}>
              <div className={styles.summarySection}>
                <p style={{ marginBottom: '12px', color: '#666' }}>
                  Edit the meeting summary below. You can modify the content before sending it to the chat
                </p>
                <TextArea
                  value={editableSummary}
                  onChange={(e) => setEditableSummary(e.target.value)}
                  placeholder="Meeting summary will appear here processing from AI wait for few seconds..."
                  rows={20}
                  className={styles.summaryTextArea}
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.8',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>
          </TabPane>
        </Tabs>
      </Modal>
    </>
  )
}
export default PatientChatBubble
